import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, Lock, Clock } from "lucide-react";
import { toast } from "sonner";
import { getHub, trackHub } from "../lib/hub";
import { HubIcon } from "../components/HubIcon";
import { useLanguage, LANGUAGES } from "../context/LanguageContext";

const isExternal = (r) => /^https?:\/\//.test(r || "");

export default function TavenHub() {
  const { lang, setLang, ml, t, action } = useLanguage();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    getHub().then(setData).catch(() => setData({ sections: [], cards: [] }));
    trackHub({ route: "/hub", event: "view" });
  }, []);

  const query = q.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!data) return [];
    if (!query) return data.cards;
    return data.cards.filter((c) =>
      [c.title, c.description].some((d) => Object.values(d || {}).join(" ").toLowerCase().includes(query))
    );
  }, [data, query]);

  const handleCard = (c, e) => {
    if (c.visibility === "coming_soon") {
      e.preventDefault();
      trackHub({ card_id: c.id, route: c.route, event: "failed" });
      toast.info(`${ml(c.title)} — ${t("comingSoon")}`);
      return;
    }
    const unavailable = c.gated || !c.route || c.route === "#";
    if (unavailable) {
      e.preventDefault();
      trackHub({ card_id: c.id, route: c.route, event: "failed" });
      toast.info(c.gated_reason || `${ml(c.title)} — ${t("gated")}`);
      return;
    }
    trackHub({ card_id: c.id, route: c.route, event: "click" });
    if (isExternal(c.route)) { window.open(c.route, "_blank", "noopener"); return; }
    navigate(c.route);
  };

  if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;

  return (
    <div data-testid="taven-hub-page" className="bg-taven-gray/20 min-h-screen">
      {/* Hero */}
      <section className="bg-taven-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(circle at 15% 30%, rgba(197,160,89,0.35), transparent 40%), radial-gradient(circle at 85% 70%, rgba(242,101,34,0.3), transparent 45%)" }} />
        <div className="relative px-4 md:px-8 lg:px-12 mx-auto max-w-7xl py-10 md:py-14">
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-taven-gold">TAVEN Ecosystem</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter font-display mt-2">{t("hubTitle")}</h1>
          <p className="mt-3 text-white/70 text-base md:text-lg max-w-2xl">{t("hubSubtitle")}</p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="flex-1 max-w-md flex items-center bg-white/10 border border-white/15 rounded-full h-12 px-4">
              <Search className="w-4 h-4 text-white/60 shrink-0" />
              <input data-testid="hub-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder={t("searchPlaceholder")}
                className="bg-transparent outline-none px-3 w-full text-sm placeholder:text-white/50" />
            </div>
            <div className="flex items-center gap-1 bg-white/10 border border-white/15 rounded-full p-1" data-testid="hub-lang-switch">
              {LANGUAGES.map((l) => (
                <button key={l.code} onClick={() => setLang(l.code)} data-testid={`hub-lang-${l.code}`}
                  className={`px-3 py-1.5 text-xs font-bold rounded-full transition-colors ${lang === l.code ? "bg-taven-gold text-black" : "text-white/70 hover:text-white"}`}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="px-4 md:px-8 lg:px-12 mx-auto max-w-7xl py-8 md:py-12">
        {filtered.length === 0 ? (
          <div className="border border-dashed border-black/15 py-24 text-center" data-testid="hub-no-results">
            <p className="text-lg font-display font-bold">{t("noResults")}</p>
          </div>
        ) : (
          data.sections.map((sec) => {
            const cards = filtered.filter((c) => c.section === sec.key);
            if (cards.length === 0) return null;
            return (
              <section key={sec.key} className="mb-10 md:mb-14" data-testid={`hub-section-${sec.key}`}>
                <h2 className="text-lg font-black font-display uppercase tracking-wide text-taven-black border-b-2 border-taven-gold/40 pb-2 mb-5">{ml(sec.title)}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {cards.map((c) => (
                    <div key={c.id} data-testid={`hub-card-${c.id}`}
                      onClick={(e) => handleCard(c, e)}
                      className="group bg-white border border-black/10 p-5 flex flex-col hover:border-taven-gold hover:shadow-lg transition-all cursor-pointer h-full">
                      <div className="flex items-start justify-between mb-3">
                        <span className="w-11 h-11 rounded-xl bg-taven-black text-taven-gold flex items-center justify-center group-hover:bg-taven-gold group-hover:text-black transition-colors">
                          <HubIcon name={c.icon} className="w-5 h-5" />
                        </span>
                        {c.visibility === "coming_soon" && (
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-black/5 text-black/60 px-2 py-1 rounded-full flex items-center gap-1"><Clock className="w-3 h-3" />{t("comingSoon")}</span>
                        )}
                        {c.visibility !== "coming_soon" && c.gated && (
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-taven-orange/10 text-taven-orange px-2 py-1 rounded-full flex items-center gap-1"><Lock className="w-3 h-3" />{t("gated")}</span>
                        )}
                      </div>
                      <h3 className="font-black font-display text-base tracking-tight">{ml(c.title)}</h3>
                      <p className="text-xs text-muted-foreground mt-1 flex-1 line-clamp-2">{ml(c.description)}</p>
                      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-taven-black group-hover:text-taven-gold mt-4">
                        {c.gated || c.visibility === "coming_soon" ? t("gated") : action(c.action)} <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            );
          })
        )}
      </div>
    </div>
  );
}
